import { FC } from 'react';
import { Content } from '@prismicio/client';
import { HubspotFormData } from '@/interfaces';
import HeroSection from './components/HeroSection';
import Eyebrow from './components/Eyebrow';
import Headline from './components/Headline';
import Description from './components/Description';
import FormCard from './components/FormCard';
import InfoCard from './components/InfoCard';

interface ContentFormSplitProps {
  slice: Content.LeadCaptureHeroSlice;
  formData: HubspotFormData | null;
}

/**
 * "Content / Form Split" variation for `LeadCaptureHero`.
 *
 * Owns only this variation's layout: desktop two-column grid (content vs
 * form), swappable column order via `form_position`, with a nested
 * responsive card grid inside the content column. Shared chrome (section
 * shell, eyebrow, headline, description, form card) comes from
 * `./components/*`.
 */
const ContentFormSplit: FC<ContentFormSplitProps> = ({ slice, formData }) => {
  const primary =
    slice.primary as Content.LeadCaptureHeroSliceContentFormSplitPrimary;
  const {
    background,
    eyebrow,
    headline,
    description,
    cards,
    form_position,
    form_title,
    footnote,
    form_id,
    form_field_style,
    form_field_layout,
    success_content,
    redirect_link,
  } = primary;

  // Default is form on the right (content left). "Left" swaps them on desktop
  // only — mobile source order (content → form) is kept so the hero reads top
  // to bottom.
  const formOnLeft = (form_position ?? 'Right').toLowerCase() === 'left';
  const contentColClass = formOnLeft
    ? 'lg:col-start-2 lg:row-start-1'
    : 'lg:col-start-1 lg:row-start-1';
  const formColClass = formOnLeft
    ? 'lg:col-start-1 lg:row-start-1'
    : 'lg:col-start-2 lg:row-start-1';

  return (
    <HeroSection
      sliceType={slice.slice_type}
      variation={slice.variation}
      backgroundImage={background}
    >
      <div className="relative z-10 mx-auto grid max-w-[1280px] grid-cols-1 gap-y-10 lg:grid-cols-2 lg:items-start lg:gap-x-[60px]">
        {/* Content column — vertically centered against the taller form card
            on desktop. Eyebrow sits 8px above the headline; headline /
            description / cards use the 28px outer gap. */}
        <div
          className={`flex w-full flex-col gap-7 lg:self-center ${contentColClass}`}
        >
          {(eyebrow || headline) && (
            <div className="flex flex-col gap-2">
              <Eyebrow text={eyebrow} />
              <Headline field={headline} />
            </div>
          )}

          <Description
            field={description}
            className="font-inter text-[16px] font-light leading-[1.5] text-white md:text-[18px]"
          />

          {/* Cards grid:
              - mobile: 1 per row
              - tablet (sm+): 2 per row — layout is still stacked above the form
              - small desktop (lg: 1024–1279): 1 per row — content column is
                narrow next to the form card
              - large desktop (xl: 1280+): 2 per row — content column has room */}
          {Array.isArray(cards) && cards.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {cards.map((card, i) => (
                <InfoCard key={i} card={card} />
              ))}
            </div>
          )}
        </div>

        <FormCard
          formData={formData}
          formTitle={form_title}
          formId={form_id}
          footnote={footnote}
          formFieldStyle={form_field_style}
          formFieldLayout={form_field_layout}
          successContent={success_content}
          redirectLink={redirect_link}
          className={formColClass}
        />
      </div>
    </HeroSection>
  );
};

export default ContentFormSplit;
